/** Consistent customer-facing terms for incident vs recurring-risk review (backend still uses pattern_*). */

import { displaySafetyManagerLabel } from '../safetyManagerUxLabels';
import { displayPatternEvidenceProofLabel } from './patternReviewDisplayLabels';
import { GROUPED_REVIEW_PAGE_SCOPE_LINE } from './groupedSafetyCaseStepLabels';
import type { ReviewWindowContext } from './reviewWindowContext';
import { formatGroupedRecurringRiskVolumeBadge } from './groupedReviewWindowCopy';

export { formatGroupedRecurringRiskVolumeBadge, formatGroupedPatternVolumeReadLine } from './groupedReviewWindowCopy';
export {
  formatObservationBadge,
  formatObservationCountLine,
  formatReviewWindowHeaderLabel,
  resolveReviewWindowContext,
  type ReviewWindowContext,
  type ReviewWindowScopeKind,
} from './reviewWindowContext';

export const RECURRING_SAFETY_RISK_LABEL = 'Recurring risk pattern';
/** @deprecated Use RECURRING_SAFETY_RISK_LABEL */
export const RECURRING_SAFETY_PATTERN_LABEL = RECURRING_SAFETY_RISK_LABEL;

export const OPEN_LINKED_RECORDS_LABEL = displaySafetyManagerLabel('open_linked_records');
export const OPEN_LINKED_RECORDS_TOOLTIP =
  'Open pattern observations currently linked to this recurring risk pattern. This can be broader than today\u2019s queue.';
export const RISK_FREQUENCY_TOOLTIP_168H =
  'All pattern observations linked to this recurring risk pattern in the selected review window.';

/** Grouped review — clarifies risk frequency is signal roll-up, not distinct confirmed incidents. */
export const PATTERN_VOLUME_SIGNALS_DISCLAIMER =
  'Pattern observations summarize repeated activity and should not be read as separate verified incidents.';

/** Parenthetical on drawer series context when risk frequency is read-side roll-up. */
export const READ_SIDE_RISK_FREQUENCY_PARENTHEETICAL = '(read-side risk frequency)';

/** Fallback when backend group_reason is empty (grouped incident review). */
export const GROUPED_INCIDENT_GROUPING_BASIS_FALLBACK =
  'Grouped by recurring risk pattern, zone, camera, and device in the selected window.';

/** Recurring-risk hourly chart empty state. */
export const RECURRING_RISK_HEATMAP_EMPTY_MESSAGE =
  'No recurring pattern observations are available for this window yet.';

/** Shown above the hourly chart — values are not risk-frequency totals. */
export const RECURRING_RISK_HEATMAP_COUNT_EXPLAINER =
  'Shows how many deduplicated occurrences of this pattern fell in each UTC hour.';

export const RECURRING_RISK_HOURLY_BAR_EXPLAINER =
  'Bar height and value show occurrences in the selected window. Active days show how consistently the pattern returned.';

export function formatHeatmapRowActiveHoursLabel(activeHourSlots: number): string {
  const n = Math.max(0, Number(activeHourSlots) || 0);
  if (n <= 0) return 'No hours with occurrences in this window';
  return `${n.toLocaleString()} active hour${n === 1 ? '' : 's'} in this window`;
}

/** Compact UTC ranges for active hours, e.g. "00:00–04:00, 15:00, 21:00–23:00 UTC". */
export function formatActiveUtcHourRanges(activeHours: number[]): string {
  const hours = Array.from(new Set(
    (activeHours || [])
      .map((h) => Number(h))
      .filter((h) => Number.isFinite(h) && h >= 0 && h <= 23),
  )).sort((a, b) => a - b);
  if (!hours.length) return 'None';

  const pad = (h: number) => `${String(h).padStart(2, '0')}:00`;
  const ranges: string[] = [];
  let start = hours[0];
  let prev = hours[0];
  for (let i = 1; i <= hours.length; i += 1) {
    const current = hours[i];
    if (current === prev + 1) {
      prev = current;
      continue;
    }
    ranges.push(start === prev ? pad(start) : `${pad(start)}–${pad(prev)}`);
    start = current;
    prev = current;
  }
  return `${ranges.join(', ')} UTC`;
}

export function formatHeatmapCellCountLabel(occurrenceCount: number): string {
  const n = Math.max(0, Number(occurrenceCount) || 0);
  if (n <= 0) return '';
  return n === 1 ? '1 occurrence' : `${n.toLocaleString()} occurrences`;
}

export function heatmapCellCountTooltip(args: {
  rowLabel: string;
  hour: number;
  occurrenceCount: number;
  activeDayCount?: number;
  windowDayCount?: number;
}): string {
  const occurrences = Math.max(0, Number(args.occurrenceCount) || 0);
  const activeDays = Math.max(0, Number(args.activeDayCount) || 0);
  const windowDays = Math.max(1, Number(args.windowDayCount) || 7);
  const hourText = `${String(args.hour).padStart(2, '0')}:00 UTC`;
  if (occurrences <= 0) return `${args.rowLabel} · ${hourText} · no occurrences in this window`;
  const occurrenceLabel = formatHeatmapCellCountLabel(occurrences);
  const activeDayLabel = activeDays === 1 ? '1 active day' : `${activeDays} active days`;
  return `${args.rowLabel} · ${hourText} · ${occurrenceLabel} · ${activeDayLabel} of ${windowDays}`;
}

/** Full-page grouped review workspace hero hint. */
export const GROUPED_REVIEW_WORKSPACE_HINT =
  'Review patterns, inspect representative evidence, and decide what happens next.';
/** @deprecated Use RISK_FREQUENCY_TOOLTIP_168H */
export const PATTERN_VOLUME_TOOLTIP_168H = RISK_FREQUENCY_TOOLTIP_168H;

export const RISK_REVIEW_LABEL = 'Risk pattern review';
/** @deprecated Use RISK_REVIEW_LABEL */
export const PATTERN_REVIEW_LABEL = RISK_REVIEW_LABEL;

export const RISK_DECISION_LABEL = 'Risk decision';
/** @deprecated Use RISK_DECISION_LABEL */
export const PATTERN_DECISION_LABEL = RISK_DECISION_LABEL;

/** Secondary label inside the representative audit accordion. */
export const DEEP_REVIEW_AUDIT_LABEL = 'Deep review checklist';
export const REPRESENTATIVE_AUDIT_LABEL = 'Review representative evidence';
export const REPRESENTATIVE_AUDIT_SUBTITLE =
  'Representative evidence sample for review — evidence, workflow, and closeout for this recurring risk pattern.';

export const RISK_OWNER_ROLE_HELPER =
  'Accountable for this recurring risk pattern.';

export const ACTION_OWNER_ROLE_HELPER =
  'Responsible for follow-up or corrective action.';

/** Shown on Understand risk when action owner is not set yet. */
export const ACTION_OWNER_ASSIGN_STEP_HINT =
  'Assigned in the corrective action step.';

export function ownerRoleIsUnassigned(label?: string | null): boolean {
  const value = String(label || '').trim();
  return (
    !value
    || /^unassigned$/i.test(value)
    || /^not assigned$/i.test(value)
    || /^none$/i.test(value)
    || /^not set$/i.test(value)
  );
}

export function buildRiskDecisionRecommendedNextStep(args: {
  patternOwnerLabel?: string | null;
  followUpOwnerLabel?: string | null;
}): string {
  const patternMissing = ownerRoleIsUnassigned(args.patternOwnerLabel);
  if (patternMissing) {
    return 'Assign a risk owner on this card, then continue to Evidence review.';
  }
  return 'Continue to Evidence review, then decide whether this pattern requires management.';
}

export function buildGroupedEvidenceStatusChipLabel(args: {
  evidenceReviewIsCurrent?: boolean;
  evidenceStatusLabel?: string | null;
}): string {
  if (args.evidenceReviewIsCurrent) {
    return 'Evidence review: Current';
  }
  const proof = displayPatternEvidenceProofLabel(args.evidenceStatusLabel);
  if (proof && !/^(pending|not evaluated|needs review)$/i.test(proof)) {
    return `Evidence: ${proof}`;
  }
  return 'Evidence pending review';
}

export function buildGroupedWhatToKnowFirst(_args: {
  patternOwnerLabel?: string | null;
  attentionReasonSummary?: string;
  reviewFocusSummary?: string;
}): string {
  return GROUPED_REVIEW_PAGE_SCOPE_LINE;
}

export function buildGroupedPersistentNextStepLabel(args: {
  representativeReviewComplete?: boolean;
  evidenceConfirmed?: boolean;
  patternOwnerLabel?: string | null;
  followUpOwnerLabel?: string | null;
  followUpMeasured?: boolean;
  reportReady?: boolean;
}): string {
  if (!args.representativeReviewComplete) {
    return 'Review representative evidence and confirm whether this recurring risk pattern requires corrective action.';
  }
  if (!args.evidenceConfirmed) {
    return 'Confirm the risk pattern in Evidence review before starting corrective action.';
  }
  if (ownerRoleIsUnassigned(args.patternOwnerLabel)) {
    return 'Assign a risk owner in Risk pattern, then continue to Corrective action.';
  }
  if (ownerRoleIsUnassigned(args.followUpOwnerLabel)) {
    return 'Open Corrective action to assign an action owner and route follow-up.';
  }
  if (!args.followUpMeasured) {
    return 'Record impact in Impact measurement before treating this recurring risk pattern as reduced.';
  }
  if (args.reportReady) {
    return 'Open Report preparation and review the Safety Report draft plus supporting proof package.';
  }
  return 'Open Report preparation for this recurring risk pattern.';
}

export function shouldPrioritizeAssignRiskOwnerAction(args: {
  patternOwnerLabel?: string | null;
  workflowLabel?: string | null;
}): boolean {
  void args.workflowLabel;
  return ownerRoleIsUnassigned(args.patternOwnerLabel);
}

export function assignRiskOwnerActionLabel(patternOwnerLabel?: string | null): string {
  const label = displaySafetyManagerLabel('pattern_owner');
  if (ownerRoleIsUnassigned(patternOwnerLabel)) {
    return `Assign ${label.toLowerCase()}`;
  }
  return `Update ${label.toLowerCase()}`;
}

export function assignActionOwnerActionLabel(): string {
  return `Assign ${displaySafetyManagerLabel('followup_owner').toLowerCase()}`;
}
export const REPRESENTATIVE_INCIDENT_LABEL = 'Representative evidence';

/** Static baseline for Risk Decision — write scope is always open + selected window. */
export const RISK_ACTION_SCOPE_BASELINE =
  'Actions apply to open linked records in the selected review window.';
/** @deprecated Use RISK_ACTION_SCOPE_BASELINE */
export const PATTERN_ACTION_SCOPE_BASELINE = RISK_ACTION_SCOPE_BASELINE;

export type PatternVolumeKindLabel = 'persisted_episode' | 'review_window' | 'single';

export function formatPatternVolumeCount(
  count: number,
  _kind: PatternVolumeKindLabel = 'review_window',
): string {
  const n = Math.max(0, Number(count) || 0);
  if (n <= 1) {
    return `${n.toLocaleString()} pattern observation${n === 1 ? '' : 's'}`;
  }
  return `${n.toLocaleString()} pattern observations linked to this recurring risk pattern`;
}

/** @deprecated Use formatObservationBadge with ReviewWindowContext. */
export function formatGroupedRecurringRiskVolumeLabel(
  count: number,
  reviewWindowContext: ReviewWindowContext,
): string {
  return formatGroupedRecurringRiskVolumeBadge(count, reviewWindowContext);
}

export function patternVolumeDisplayLine(
  count: number,
  kind: PatternVolumeKindLabel = 'review_window',
): string {
  return `${displaySafetyManagerLabel('pattern_volume')}: ${formatPatternVolumeCount(count, kind)}`;
}

export function patternActionScopeDisplayLine(
  matchedCount: number | null | undefined,
  options?: { pending?: boolean },
): string {
  if (options?.pending || matchedCount == null) {
    return 'Action scope: Refresh count to resolve affected open linked records';
  }
  const n = Math.max(0, Number(matchedCount) || 0);
  return `Action scope: ${n.toLocaleString()} open linked record${n === 1 ? '' : 's'} in the selected review window`;
}

/** Risk Decision card — action scope before dry-run in the modal. */
export function patternDecisionActionScopePendingLine(): string {
  return 'Action scope: Refresh count in Close / Assign / Suppress to resolve the affected open linked records';
}

export function patternVolumeActionScopeWarningCopy(
  _patternVolume: number,
  _actionScope: number,
): { title: string; body: string; footer: string } {
  return {
    title: 'Risk frequency and action scope differ',
    body:
      `${displaySafetyManagerLabel('pattern_volume')} is the total pattern observation count for this recurring risk pattern. `
      + 'Action scope is the number of open linked records in the selected review window that this action will change.',
    footer:
      'This action will not change closed or suppressed observations outside the selected time window.',
  };
}

export function closePatternConfirmButtonLabel(matchedCount: number): string {
  const n = Math.max(0, Number(matchedCount) || 0);
  return `Close ${n.toLocaleString()} open linked record${n === 1 ? '' : 's'}`;
}

export function assignPatternConfirmButtonLabel(matchedCount: number): string {
  const n = Math.max(0, Number(matchedCount) || 0);
  if (n <= 0) return 'Assign owner';
  return `Assign owner (${n.toLocaleString()})`;
}

/** One-line assign-dialog scope summary (buyer-facing). */
export function assignOwnerScopeSummaryLine(
  matchedCount: number | null | undefined,
  options?: { pending?: boolean; windowLabel?: string | null },
): string {
  if (options?.pending || matchedCount == null) {
    return 'Counting open observations in this review window…';
  }
  const n = Math.max(0, Number(matchedCount) || 0);
  const window = String(options?.windowLabel || '').trim();
  const base = n === 1
    ? 'Sets the risk owner on 1 open observation'
    : `Sets the risk owner on ${n.toLocaleString()} open observations`;
  return window ? `${base} · ${window}` : `${base} in this review window`;
}

export function suppressPatternConfirmButtonLabel(
  matchedCount: number,
  closeOpenMembers: boolean,
): string {
  const n = Math.max(0, Number(matchedCount) || 0);
  if (closeOpenMembers) {
    return `Suppress similar observations and close ${n.toLocaleString()} open linked record${n === 1 ? '' : 's'}`;
  }
  return 'Suppress similar observations';
}

export function closePatternPendingButtonLabel(): string {
  return 'Close recurring risk pattern…';
}

export function assignPatternPendingButtonLabel(): string {
  return `Assign ${displaySafetyManagerLabel('pattern_owner').toLowerCase()}…`;
}

export function suppressPatternPendingButtonLabel(): string {
  return 'Suppress similar observations…';
}

export function patternActionConfirmationIntro(
  mode: 'close' | 'assign' | 'suppress',
  matchedCount: number,
  patternVolume: number,
): string {
  const n = Math.max(0, Number(matchedCount) || 0);
  const volume = Math.max(0, Number(patternVolume) || 0);
  const verb =
    mode === 'close' ? 'closing' : mode === 'assign' ? 'assigning' : 'suppressing against';
  let text = `You are ${verb} ${n.toLocaleString()} open linked record${n === 1 ? '' : 's'} in the selected review window.`;
  if (volume > 0 && volume !== n) {
    text += ` This does not close the full pattern frequency total of ${volume.toLocaleString()} pattern observations.`;
  }
  text += ' Closed or suppressed observations outside the selected time window are not changed.';
  return text;
}

export function formatRelatedOccurrenceCount(count: number): string {
  const n = Math.max(0, Number(count) || 0);
  return `${n.toLocaleString()} pattern observation${n === 1 ? '' : 's'}`;
}

export function patternRollupExplanation(
  count: number,
  zoneLabel: string,
  kind: PatternVolumeKindLabel = 'review_window',
): string {
  const n = Math.max(0, Number(count) || 0);
  const zone = zoneLabel ? ` ${zoneLabel}` : '';
  if (n <= 1) {
    return `This recurring risk pattern represents one pattern observation${zone}.`;
  }
  if (kind === 'persisted_episode') {
    return `This recurring risk pattern includes ${n.toLocaleString()} pattern observations${zone} in lookback. Group actions still apply only to open linked records in the selected review window.`;
  }
  return `This recurring risk pattern includes ${n.toLocaleString()} observations${zone} in the selected review window so you can decide once before opening individual evidence records.`;
}

export function patternQueueVolumeSummary(count: number, groupingBasis?: string): string {
  const n = Math.max(0, Number(count) || 0);
  if (n <= 1) return '';
  const basis = String(groupingBasis || '').trim();
  const prefix = `This queue item combines ${n.toLocaleString()} pattern observations`;
  if (!basis) {
    return `${prefix} for the same zone, camera stream, and edge device in this review window.`;
  }
  return `${prefix} because ${basis.charAt(0).toLowerCase()}${basis.slice(1)}`;
}

export function representativeIncidentAuditHint(incidentId?: string): string {
  const id = String(incidentId || '').trim();
  if (!id) {
    return 'Use representative evidence audit for evidence, workflow history, and proof — not for the primary risk decision.';
  }
  return `Representative evidence ${id} carries evidence and audit detail for this recurring risk pattern. Use deep review when you need proof or investigation depth.`;
}

export function patternActionScopeHint(count: number | null | undefined): string {
  if (count == null) {
    return `${RISK_ACTION_SCOPE_BASELINE} Confirm dry-run to see how many open rows match.`;
  }
  const n = Math.max(0, Number(count) || 0);
  if (n > 0) {
    return `${RISK_ACTION_SCOPE_BASELINE} Dry-run resolved ${n.toLocaleString()} open linked record${n === 1 ? '' : 's'} (not the full risk frequency total).`;
  }
  return `${RISK_ACTION_SCOPE_BASELINE} Not just the representative evidence sample.`;
}

export function observedOccurrencesInWindowCopy(count: number): string {
  const n = Math.max(0, Number(count) || 0);
  return `${n.toLocaleString()} pattern observation${n === 1 ? '' : 's'} observed in this review window.`;
}

export function backendResolvedActionScopeCopy(count: number): string {
  const n = Math.max(0, Number(count) || 0);
  return `Backend resolved ${n.toLocaleString()} matching open linked record${n === 1 ? '' : 's'} for this action.`;
}

export const RISK_OWNER_LABEL = displaySafetyManagerLabel('pattern_owner');
/** @deprecated Use RISK_OWNER_LABEL */
export const PATTERN_OWNER_LABEL = RISK_OWNER_LABEL;
export const PATTERN_TEAM_LABEL = 'Risk team';
export const FOLLOWUP_OWNER_LABEL = displaySafetyManagerLabel('followup_owner');
export const FOLLOWUP_TEAM_LABEL = 'Action team';
export const ODOO_ASSIGNEE_SCOPE_NOTE = 'Odoo assignee is managed separately on the work order.';

export function normalizeGroupingBasis(rawReason: string): string {
  const raw = String(rawReason || '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (lower.includes('exact incident type')) {
    return 'Grouped by matching incident type, zone, camera, and device in the selected window.';
  }
  if (
    lower.includes('matching incident pattern')
    || lower.includes('recurring safety pattern')
  ) {
    return GROUPED_INCIDENT_GROUPING_BASIS_FALLBACK;
  }
  return raw;
}

/** Fast placeholder until GET /groups returns enriched pattern metadata. */
export function buildFallbackGroupContextFromIncident(incident: {
  incident_id?: string | null;
  incident_type?: string | null;
  zone_id?: string | null;
  camera_id?: string | null;
  device_id?: string | null;
  policy_id?: string | null;
  policy_name?: string | null;
  severity?: string | null;
  status?: string | null;
  raw?: { alert_count?: number | null } | null;
  metrics?: { alert_count?: number | null } | null;
} | null | undefined): Record<string, unknown> | null {
  const incidentId = String(incident?.incident_id || '').trim();
  if (!incidentId) return null;
  const alertCount = Math.max(
    1,
    Number(
      (incident as any)?.alert_count
        || incident?.raw?.alert_count
        || incident?.metrics?.alert_count
        || 1,
    ),
  );
  const groupKey = [
    incident?.incident_type || 'unknown',
    incident?.zone_id || '-',
    incident?.camera_id || '-',
    incident?.device_id || '-',
  ].join('|');
  return {
    group_key: groupKey,
    incident_type: incident?.incident_type || null,
    zone_id: incident?.zone_id || null,
    camera_id: incident?.camera_id || null,
    device_id: incident?.device_id || null,
    policy_id: incident?.policy_id || null,
    policy_name: incident?.policy_name || null,
    severity: incident?.severity || null,
    status: incident?.status || null,
    incident_id: incidentId,
    linked_detailed_record_id: incidentId,
    alert_count: alertCount,
    is_grouped: alertCount > 1,
  };
}

export function patternBucketSummaryText(
  occurrenceCount?: number | null,
  bucketCount?: number | null,
  kind: PatternVolumeKindLabel = 'review_window',
): string {
  const occurrences = Math.max(0, Number(occurrenceCount || 0));
  const buckets = Math.max(0, Number(bucketCount || 0));
  const volumePhrase =
    kind === 'persisted_episode'
      ? `${occurrences.toLocaleString()} pattern observations (${displaySafetyManagerLabel('pattern_volume').toLowerCase()})`
      : `${occurrences.toLocaleString()} pattern observation${occurrences === 1 ? '' : 's'} in the selected review window`;
  if (occurrences > 0 && buckets > 0) {
    return `${volumePhrase} grouped into ${buckets.toLocaleString()} time bucket${buckets === 1 ? '' : 's'}.`;
  }
  if (occurrences > 0) {
    return `${volumePhrase}.`;
  }
  if (buckets > 0) {
    return `${buckets.toLocaleString()} grouped time bucket${buckets === 1 ? '' : 's'} in the selected review window.`;
  }
  return '';
}
